import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RFB from "@novnc/novnc";
import {
  ArrowLeft,
  Cpu,
  Home,
  MonitorUp,
  Power,
  RefreshCcw,
  RotateCcw,
  ShieldCheck,
  Smartphone,
  Upload,
  Wifi,
  WifiOff
} from "lucide-react";

type ConnectionState = "connecting" | "connected" | "disconnected" | "error";

type Health = {
  ok: boolean;
  bootCompleted?: boolean;
  adbDevices?: string;
  emulator?: string;
  uptime?: string;
  checkedAt: string;
  error?: string;
};

const API_BASE = import.meta.env.VITE_API_BASE || "https://android-api.darchei.tech";
const STREAM_URL =
  import.meta.env.VITE_STREAM_URL || "wss://android-stream.darchei.tech/websockify";

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function describeError(error: unknown) {
  const message = error instanceof Error ? error.message : "Request failed";
  if (message === "Failed to fetch") {
    return `Backend unreachable. Configure Cloudflare DNS/Tunnel and start the EC2 Android host for ${API_BASE}.`;
  }
  return message;
}

async function apiFetch(path: string, init?: RequestInit) {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.body instanceof File ? {} : { "Content-Type": "application/json" }),
      ...init?.headers
    }
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with ${response.status}`);
  }

  const contentType = response.headers.get("content-type") || "";
  return contentType.includes("application/json") ? response.json() : response.text();
}

export default function App() {
  const screenRef = useRef<HTMLDivElement | null>(null);
  const rfbRef = useRef<RFB | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  const [health, setHealth] = useState<Health | null>(null);
  const [activity, setActivity] = useState("Bootstrapping remote display");
  const [busyCommand, setBusyCommand] = useState<string | null>(null);
  const [uploadName, setUploadName] = useState<string | null>(null);

  const connect = useCallback(() => {
    if (!screenRef.current) return;

    rfbRef.current?.disconnect();
    screenRef.current.innerHTML = "";
    setConnection("connecting");
    setActivity("Connecting to Android display");

    const rfb = new RFB(screenRef.current, STREAM_URL, { credentials: {} });
    rfb.viewOnly = false;
    rfb.scaleViewport = true;
    rfb.resizeSession = false;
    rfb.focusOnClick = true;

    rfb.addEventListener("connect", () => {
      setConnection("connected");
      setActivity("Display connected");
    });
    rfb.addEventListener("disconnect", () => {
      setConnection((current) => (current === "error" ? current : "disconnected"));
      setActivity("Display disconnected");
    });
    rfb.addEventListener("securityfailure", () => {
      setConnection("error");
      setActivity("Display authentication failed");
    });

    rfbRef.current = rfb;
  }, []);

  const refreshHealth = useCallback(async () => {
    try {
      const data = (await apiFetch("/health")) as Health;
      setHealth(data);
    } catch (error) {
      setHealth({
        ok: false,
        checkedAt: new Date().toISOString(),
        error: describeError(error)
      });
    }
  }, []);

  useEffect(() => {
    connect();
    refreshHealth();
    const interval = window.setInterval(refreshHealth, 15000);
    return () => {
      window.clearInterval(interval);
      rfbRef.current?.disconnect();
    };
  }, [connect, refreshHealth]);

  const runCommand = async (label: string, path: string) => {
    setBusyCommand(label);
    setActivity(label);
    try {
      await apiFetch(path, { method: "POST", body: JSON.stringify({}) });
      await refreshHealth();
      if (path.includes("restart")) connect();
      setActivity(`${label} sent`);
    } catch (error) {
      setActivity(describeError(error));
    } finally {
      setBusyCommand(null);
    }
  };

  const installApk = async (file: File) => {
    setBusyCommand("Installing APK");
    setUploadName(file.name);
    setActivity(`Uploading ${file.name}`);

    try {
      await apiFetch("/apps/install", {
        method: "POST",
        body: file,
        headers: {
          "Content-Type": "application/vnd.android.package-archive",
          "X-File-Name": encodeURIComponent(file.name)
        }
      });
      await refreshHealth();
      setActivity(`${file.name} installed`);
    } catch (error) {
      setActivity(describeError(error));
    } finally {
      setBusyCommand(null);
    }
  };

  const status = useMemo(() => {
    if (connection === "connected" && health?.ok) return "Ready";
    if (connection === "connected") return "Display online";
    if (connection === "connecting") return "Connecting";
    return "Offline";
  }, [connection, health?.ok]);

  return (
    <main className="app-shell">
      <section className="workspace">
        <header className="topbar">
          <div className="brand">
            <span className="brand-mark">
              <Smartphone size={20} strokeWidth={2.2} />
            </span>
            <div>
              <h1>Android Emulator</h1>
              <p>{status}</p>
            </div>
          </div>

          <div className="status-strip" aria-live="polite">
            <span className={cn("status-dot", connection)} />
            <span>{activity}</span>
          </div>
        </header>

        <div className="content-grid">
          <aside className="control-rail" aria-label="Device controls">
            <button
              className="icon-button primary"
              title="Reconnect display"
              aria-label="Reconnect display"
              onClick={connect}
            >
              <MonitorUp size={19} />
            </button>
            <button
              className="icon-button"
              title="Refresh health"
              aria-label="Refresh health"
              onClick={refreshHealth}
            >
              <RefreshCcw size={19} />
            </button>
            <button
              className="icon-button"
              title="Android Back"
              aria-label="Android Back"
              disabled={!!busyCommand}
              onClick={() => runCommand("Back", "/control/back")}
            >
              <ArrowLeft size={19} />
            </button>
            <button
              className="icon-button"
              title="Android Home"
              aria-label="Android Home"
              disabled={!!busyCommand}
              onClick={() => runCommand("Home", "/control/home")}
            >
              <Home size={19} />
            </button>
            <button
              className="icon-button"
              title="Power"
              aria-label="Power"
              disabled={!!busyCommand}
              onClick={() => runCommand("Power", "/control/power")}
            >
              <Power size={19} />
            </button>
            <button
              className="icon-button caution"
              title="Restart emulator"
              aria-label="Restart emulator"
              disabled={!!busyCommand}
              onClick={() => runCommand("Restarting emulator", "/control/restart")}
            >
              <RotateCcw size={19} />
            </button>
            <button
              className="icon-button upload"
              title="Install APK"
              aria-label="Install APK"
              disabled={!!busyCommand}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={19} />
            </button>
            <input
              ref={fileInputRef}
              className="hidden-input"
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) void installApk(file);
              }}
            />
          </aside>

          <section className="device-stage" aria-label="Android screen">
            <div className="device-frame">
              <div ref={screenRef} className="vnc-screen" />
              {connection !== "connected" && (
                <div className="screen-overlay">
                  {connection === "connecting" ? <Wifi size={28} /> : <WifiOff size={28} />}
                  <span>{activity}</span>
                </div>
              )}
            </div>
          </section>

          <aside className="telemetry" aria-label="Runtime status">
            <section className="panel">
              <div className="panel-heading">
                <Cpu size={17} />
                <h2>Runtime</h2>
              </div>
              <dl className="metric-list">
                <div>
                  <dt>Display</dt>
                  <dd>{connection}</dd>
                </div>
                <div>
                  <dt>ADB</dt>
                  <dd>{health?.ok ? "online" : "offline"}</dd>
                </div>
                <div>
                  <dt>Boot</dt>
                  <dd>{health?.bootCompleted ? "complete" : "pending"}</dd>
                </div>
                <div>
                  <dt>Upload</dt>
                  <dd>{uploadName || "none"}</dd>
                </div>
              </dl>
            </section>

            <section className="panel">
              <div className="panel-heading">
                <ShieldCheck size={17} />
                <h2>Access</h2>
              </div>
              <p className="compact-copy">Cloudflare Access protects the UI, stream, and API hostnames.</p>
            </section>

            <section className="panel">
              <div className="panel-heading">
                <Wifi size={17} />
                <h2>Health</h2>
              </div>
              <pre className="health-log">
                {health ? JSON.stringify(health, null, 2) : "waiting for first check"}
              </pre>
            </section>
          </aside>
        </div>
      </section>
    </main>
  );
}
